import { SECTION_CODES, BASE_SECTION_CODES, COUNTRIES } from '@/constants';

export function getTokensFromPath(path) {
  return path.split('#');
}

export function getBaseToken(path) {
  return getTokensFromPath(path)[0];
}

export function getPathFromTokens(tokens) {
  return tokens.join('#');
}

export function getLabelTokens(label) {
  return (label || '').split('|').map((i) => i.trim());
}

function createLabelFromTokens(tokens = []) {
  return tokens.join(' | ');
}

const sectionFilters = {
  [SECTION_CODES.RESIDENCE_LENGTH]: (option) => getTokensFromPath(option.value).length <= 4,
  [SECTION_CODES.GENDER]: (option) => option.value !== '0001#0048#0052',
};

const noop = (i) => i;

export function isDemographicSection(path) {
  return getBaseToken(path) === BASE_SECTION_CODES.DEMOGRAPHIC;
}

export function isInterestSection(path) {
  return getBaseToken(path) === BASE_SECTION_CODES.INTERESTS;
}

export function isSubSection(option) {
  const subSectionLength = isInterestSection(option.value) ? 4 : 5;
  return getLabelTokens(option.label).length === subSectionLength;
}

export function getSectionId(section) {
  return section.parentCode;
}

export function getOptions(section) {
  return section?.childNodes;
}

function getParentLabel(label) {
  return createLabelFromTokens(getLabelTokens(label).slice(0, -1));
}

export function isSection(section) {
  return !!section && Array.isArray(getOptions(section)) && !!getSectionId(section);
}

export function getParentPath(path) {
  return getPathFromTokens(getTokensFromPath(path).slice(0, -1));
}

export function getSectionPath(key) {
  const CODES = Object.values(SECTION_CODES);

  if (!key) {
    return key;
  }

  const sectionKey = CODES.find((code) => code === key);

  return sectionKey || getSectionPath(getParentPath(key));
}

export function getSectionKey(key) {
  return getSectionPath(key) || getParentPath(key) || key;
}

export function getDefaultTargetingInfo() {
  return {
    [SECTION_CODES.COUNTRY]: COUNTRIES.UK,
  };
}

export function isPath(path) {
  return !!path && !getTokensFromPath(path).some((token) => token.length !== 4);
}

export function getLabelForSection(sectionName) {
  return getLabelTokens(sectionName)?.pop();
}

function childNodeOptionToItem(option) {
  const [[value, label]] = Object.entries(option);
  return { value, label };
}

function findOptionIndexInArray(arr = [], values = {}) {
  const [[key, value]] = Object.entries(values); // only first, for now
  const compare = (res) => res && res[key] === value;
  const lastIdx = arr.length - 1;
  const lastResult = arr[lastIdx];
  return compare(lastResult) ? lastIdx : arr.findIndex(compare);
}

function createSubSectionRoot(option, results, options, create) {
  /* eslint-disable no-bitwise */
  const parentId = getParentPath(option.value);
  let parentIdx = findOptionIndexInArray(results, { value: parentId });
  if (!~parentIdx) {
    const optsParentIdx = findOptionIndexInArray(options, { value: parentId });

    if (~optsParentIdx) {
      parentIdx = create(options[optsParentIdx]);
    } else {
      // can't find parent node for this sub-section, so now we had to find it by using labels
      const parentLabel = getParentLabel(option.label);
      const optsParentIdxByLabel = findOptionIndexInArray(options, { label: parentLabel });

      if (!~optsParentIdxByLabel) {
        // can't find parent even by label, so let's use it as option without sub-section
        return null;
      }

      const optsParentByLabel = options[optsParentIdxByLabel];
      const parentByLabelIdx = findOptionIndexInArray(results, {
        value: optsParentByLabel.value,
      });

      parentIdx = ~parentByLabelIdx ? parentByLabelIdx : create(optsParentByLabel);
    }
  }

  /* eslint-enable no-bitwise */

  return results[parentIdx];
}

function mapOptionWithNestedSubSections(option, results, options, mapper) {
  const map = (opts) => mapper({ ...opts });
  const mappedOption = map(option);

  if (!isSubSection(option)) {
    return results.concat(mappedOption);
  }

  const mapRoot = (root) => results.push(map(root));
  const subSectionRoot = createSubSectionRoot(option, results, options, mapRoot);

  if (!subSectionRoot) {
    return results.concat(mappedOption);
  }

  const subSectionOptions = subSectionRoot.childNodes || [];
  subSectionRoot.childNodes = subSectionOptions.concat(mappedOption);

  return results;
}

export function mapOptions(section, mapper = noop) {
  if (!isSection(section)) {
    return [];
  }

  const filter = sectionFilters[getSectionId(section)];
  const options = getOptions(section).map(childNodeOptionToItem);
  return options
    .filter((option) => !filter || filter(option))
    .reduce(
      (results, option) => mapOptionWithNestedSubSections(option, results, options, mapper),
      [],
    );
}
